import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Association, User } from '../types';
import api from '../services/api';
import Layout from '../components/Layout';

const AssociationDetail: React.FC = () => {
  const { associationId } = useParams<{ associationId: string }>();
  const navigate = useNavigate();

  const [association, setAssociation] = useState<Association | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [freezeConfirmOpen, setFreezeConfirmOpen] = useState(false);
  const [freezeSubmitting, setFreezeSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Form state for adding user
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [isBoardMember, setIsBoardMember] = useState(false);
  const [userType, setUserType] = useState<'homeowner' | 'tenant'>('homeowner');

  // Form state for editing user
  const [editEmail, setEditEmail] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editUnitNumber, setEditUnitNumber] = useState('');
  const [editIsBoardMember, setEditIsBoardMember] = useState(false);
  const [editIsGroupAdmin, setEditIsGroupAdmin] = useState(false);
  const [editIsVerified, setEditIsVerified] = useState(false);
  const [editUserType, setEditUserType] = useState<'homeowner' | 'tenant'>('homeowner');

  const loadData = useCallback(async () => {
    if (!associationId) return;

    setLoading(true);
    setError('');

    try {
      const [associationRes, usersRes] = await Promise.all([
        api.getAssociation(associationId),
        api.getAssociationUsers(associationId),
      ]);

      if (associationRes.success) {
        setAssociation(associationRes.data);
      }

      if (usersRes.success) {
        // Sort users by creation date - newest first
        const sortedUsers = usersRes.data.sort((a: User, b: User) => b.createdAt - a.createdAt);
        setUsers(sortedUsers);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [associationId]);

  useEffect(() => {
    if (associationId) {
      loadData();
    }
  }, [associationId, loadData]);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!associationId) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await api.onboardUser(associationId, {
        email,
        fullName,
        unitNumber,
        isBoardMember,
        type: userType,
      });

      if (response.success) {
        setModalOpen(false);
        resetForm();
        loadData();
        alert('User added successfully! Verification email has been sent.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setFullName('');
    setUnitNumber('');
    setIsBoardMember(false);
    setUserType('homeowner');
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setEditEmail(user.email);
    setEditFullName(user.fullName);
    setEditUnitNumber(user.group?.unitNumber || '');
    setEditIsBoardMember(user.isBoardMember);
    setEditIsGroupAdmin(user.isGroupAdmin);
    setEditIsVerified(user.isVerified);
    setEditUserType(user.type);
    setEditModalOpen(true);
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await api.updateUser(editingUser.id, {
        email: editEmail,
        fullName: editFullName,
        unitNumber: editUnitNumber,
        isBoardMember: editIsBoardMember,
        isGroupAdmin: editIsGroupAdmin,
        isVerified: editIsVerified,
        type: editUserType,
      });

      if (response.success) {
        setEditModalOpen(false);
        setEditingUser(null);
        loadData();
        alert('User updated successfully!');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleFreezeToggle = async () => {
    if (!association || !associationId) return;

    setFreezeSubmitting(true);

    try {
      const response = await api.freezeAssociation(associationId, !association.isFrozen);

      if (response.success) {
        setFreezeConfirmOpen(false);
        showToast(
          association.isFrozen
            ? `${association.name} has been unfrozen`
            : `${association.name} has been frozen`,
          'success'
        );
        await loadData();
      }
    } catch (err: any) {
      setFreezeConfirmOpen(false);
      showToast(err.response?.data?.message || 'Failed to update association', 'error');
    } finally {
      setFreezeSubmitting(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${user.fullName}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setError('');

    try {
      const response = await api.deleteUser(user.id);

      if (response.success) {
        loadData();
        alert('User deleted successfully!');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
      alert('Failed to delete user: ' + (err.response?.data?.message || 'Unknown error'));
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-16">
          <div className="inline-block w-12 h-12 border-4 border-heynay-blue border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!association) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          Association not found
        </div>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 border border-heynay-border rounded-2xl text-heynay-dark-gray font-medium hover:bg-heynay-light-gray transition-colors"
        >
          Back to Associations
        </button>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl shadow-lg font-medium text-white ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 border border-heynay-border rounded-2xl text-heynay-dark-gray font-medium hover:bg-heynay-light-gray transition-colors"
        >
          ← Back to Associations
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Association Info */}
        <div className="md:col-span-1">
          <div className="bg-white border border-heynay-border rounded-2xl p-6">
            {association.image && (
              <div className="mb-4 text-center">
                <img
                  src={association.image}
                  alt={association.name}
                  className="max-h-48 rounded-xl mx-auto"
                />
              </div>
            )}
            <div className="flex items-start gap-2 mb-6">
              <h4 className="text-2xl font-bold text-heynay-dark-gray">{association.name}</h4>
              {association.isFrozen && (
                <span className="mt-1 inline-block bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-lg shrink-0">
                  Frozen
                </span>
              )}
            </div>
            <div className="space-y-4">
              <div className="pb-4 border-b border-heynay-border">
                <p className="text-xs text-heynay-gray uppercase mb-1">Association ID</p>
                <p className="text-sm break-all text-heynay-dark-gray">{association.associationId}</p>
              </div>
              <div className="pb-4 border-b border-heynay-border">
                <p className="text-xs text-heynay-gray uppercase mb-1">Total Users</p>
                <p className="text-sm font-bold text-heynay-dark-gray">{users.length}</p>
              </div>
              <div>
                <button
                  onClick={() => setFreezeConfirmOpen(true)}
                  className={`w-full font-bold py-2.5 rounded-3xl transition-opacity text-sm text-white hover:opacity-90 ${
                    association.isFrozen ? 'bg-green-500' : 'bg-red-500'
                  }`}
                >
                  {association.isFrozen ? 'Unfreeze HOA' : 'Freeze HOA'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="md:col-span-2">
          <div className="bg-white border border-heynay-border rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h5 className="text-2xl font-bold text-heynay-dark-gray mb-1">Users</h5>
                <p className="text-heynay-gray text-sm">Manage users in this association</p>
              </div>
              <button
                onClick={() => setModalOpen(true)}
                className="bg-heynay-blue text-white font-bold px-6 py-2.5 rounded-3xl hover:opacity-90 transition-opacity text-sm"
              >
                + Add User
              </button>
            </div>

            {users.length === 0 ? (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-6 py-12 rounded-xl text-center">
                <h6 className="font-bold mb-2">No users in this association yet</h6>
                <p className="text-sm">Click "Add User" to onboard the first user.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-heynay-border">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-heynay-dark-gray uppercase">Name</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-heynay-dark-gray uppercase">Email</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-heynay-dark-gray uppercase">Address</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-heynay-dark-gray uppercase">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-heynay-dark-gray uppercase">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-heynay-dark-gray uppercase">Created</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-heynay-dark-gray uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-heynay-dark-gray">{user.fullName}</div>
                          <div className="flex gap-1 mt-1">
                            {user.isBoardMember && (
                              <span className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded">
                                Board
                              </span>
                            )}
                            {user.isGroupAdmin && (
                              <span className="inline-block bg-gray-100 text-gray-700 text-xs font-medium px-2 py-0.5 rounded">
                                Admin
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-heynay-gray text-sm">{user.email}</td>
                        <td className="py-3 px-4 text-heynay-gray text-sm">{user.group?.unitNumber || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-block text-xs font-medium px-2 py-1 rounded ${
                            user.type === 'homeowner'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {user.type}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-block text-xs font-medium px-2 py-1 rounded ${
                            user.isVerified
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {user.isVerified ? 'Verified' : 'Pending'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-heynay-gray text-sm">{formatDate(user.createdAt)}</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditClick(user)}
                              className="px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded hover:bg-blue-600 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="px-3 py-1 bg-red-500 text-white text-xs font-medium rounded hover:bg-red-600 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-heynay-border">
              <h5 className="text-2xl font-bold text-heynay-dark-gray">Add New User</h5>
              <button
                onClick={() => setModalOpen(false)}
                className="text-heynay-gray hover:text-heynay-dark-gray text-2xl"
              >
                ×
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleAddUser}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-heynay-dark-gray font-semibold mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  disabled={submitting}
                  className="w-full border border-heynay-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="fullName" className="block text-heynay-dark-gray font-semibold mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  required
                  disabled={submitting}
                  className="w-full border border-heynay-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="unitNumber" className="block text-heynay-dark-gray font-semibold mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  id="unitNumber"
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value)}
                  placeholder="101"
                  required
                  disabled={submitting}
                  className="w-full border border-heynay-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="userType" className="block text-heynay-dark-gray font-semibold mb-2">
                  User Type *
                </label>
                <select
                  id="userType"
                  value={userType}
                  onChange={(e) => setUserType(e.target.value as 'homeowner' | 'tenant')}
                  disabled={submitting}
                  className="w-full border border-heynay-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
                >
                  <option value="homeowner">Homeowner</option>
                  <option value="tenant">Tenant</option>
                </select>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isBoardMember}
                    onChange={(e) => setIsBoardMember(e.target.checked)}
                    disabled={submitting}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-semibold text-heynay-dark-gray">Board Member</div>
                    <small className="text-heynay-gray">User will be able to create and manage posts</small>
                  </div>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 px-4 py-3 rounded-xl mb-6">
                <small className="text-heynay-dark-gray">
                  A verification email will be sent to the user. They will need to verify
                  their email and set a password before they can login.
                </small>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-heynay-blue text-white font-bold py-3 px-6 rounded-3xl hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Adding User...' : 'Add User'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    resetForm();
                  }}
                  disabled={submitting}
                  className="px-8 py-3 border border-heynay-border rounded-3xl text-heynay-dark-gray font-bold hover:bg-heynay-light-gray transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Freeze/Unfreeze Confirmation Modal */}
      {freezeConfirmOpen && association && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h5 className="text-xl font-bold text-heynay-dark-gray mb-3">
              {association.isFrozen ? `Unfreeze ${association.name}?` : `Freeze ${association.name}?`}
            </h5>
            <p className="text-heynay-gray mb-6">
              {association.isFrozen
                ? 'Board members will regain full posting access.'
                : 'Board members will lose the ability to create or edit posts until unfrozen.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleFreezeToggle}
                disabled={freezeSubmitting}
                className={`flex-1 text-white font-bold py-3 px-6 rounded-3xl hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed ${
                  association.isFrozen ? 'bg-green-500' : 'bg-red-500'
                }`}
              >
                {freezeSubmitting
                  ? (association.isFrozen ? 'Unfreezing...' : 'Freezing...')
                  : (association.isFrozen ? 'Unfreeze' : 'Freeze')}
              </button>
              <button
                type="button"
                onClick={() => setFreezeConfirmOpen(false)}
                disabled={freezeSubmitting}
                className="px-8 py-3 border border-heynay-border rounded-3xl text-heynay-dark-gray font-bold hover:bg-heynay-light-gray transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModalOpen && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-heynay-border">
              <h5 className="text-2xl font-bold text-heynay-dark-gray">Edit User</h5>
              <button
                onClick={() => setEditModalOpen(false)}
                className="text-heynay-gray hover:text-heynay-dark-gray text-2xl"
              >
                ×
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleEditUser}>
              <div className="mb-4">
                <label htmlFor="editEmail" className="block text-heynay-dark-gray font-semibold mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="editEmail"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  disabled={submitting}
                  className="w-full border border-heynay-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="editFullName" className="block text-heynay-dark-gray font-semibold mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="editFullName"
                  value={editFullName}
                  onChange={(e) => setEditFullName(e.target.value)}
                  placeholder="John Doe"
                  required
                  disabled={submitting}
                  className="w-full border border-heynay-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="editUnitNumber" className="block text-heynay-dark-gray font-semibold mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  id="editUnitNumber"
                  value={editUnitNumber}
                  onChange={(e) => setEditUnitNumber(e.target.value)}
                  placeholder="101"
                  required
                  disabled={submitting}
                  className="w-full border border-heynay-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="editUserType" className="block text-heynay-dark-gray font-semibold mb-2">
                  User Type *
                </label>
                <select
                  id="editUserType"
                  value={editUserType}
                  onChange={(e) => setEditUserType(e.target.value as 'homeowner' | 'tenant')}
                  disabled={submitting}
                  className="w-full border border-heynay-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
                >
                  <option value="homeowner">Homeowner</option>
                  <option value="tenant">Tenant</option>
                </select>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-xl space-y-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editIsBoardMember}
                    onChange={(e) => setEditIsBoardMember(e.target.checked)}
                    disabled={submitting}
                    className="mr-3"
                  />
                  <div className="font-semibold text-heynay-dark-gray">Board Member</div>
                </label>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editIsVerified}
                    onChange={(e) => setEditIsVerified(e.target.checked)}
                    disabled={submitting}
                    className="mr-3"
                  />
                  <div className="font-semibold text-heynay-dark-gray">Verified</div>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-heynay-blue text-white font-bold py-3 px-6 rounded-3xl hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Updating User...' : 'Update User'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditModalOpen(false);
                    setEditingUser(null);
                  }}
                  disabled={submitting}
                  className="px-8 py-3 border border-heynay-border rounded-3xl text-heynay-dark-gray font-bold hover:bg-heynay-light-gray transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AssociationDetail;
